import React, { memo, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Sparkle,
  ChatCircleText,
  Note,
  MoonStars,
  Clock,
  Copy,
  UserSwitch,
} from "@phosphor-icons/react";
import {
  InboxRow,
  InboxRowChip,
  InboxRowMetaRow,
  InboxRowPreview,
  InboxRowTimestamp,
  InboxRowTitle,
  InboxRowTopLine,
  InboxRowUnreadDot,
} from "../InboxStyled";

/**
 * Single inbox list row. Memoized — the inbox list page can hold up to
 * 100 rows per page (BE max page_size), so re-rendering every row on
 * every list-cache patch (poll tick, mark-reviewed echo, etc.) is
 * wasteful. The selected row is identified by `id` equality with the
 * `selectedDocumentId` from URL.
 *
 * No matter chip on the row — that decision was finalized during the
 * 2026-04-26 mockup review (handoff §11). The matter context surfaces
 * in the right-rail header instead.
 */

// 24 hours in ms — switches the timestamp from "Xm/Xh ago" to a date.
const SHORT_REL_THRESHOLD_MS = 24 * 60 * 60 * 1000;
// 6 days in ms — switches from "Xd ago" to "MMM D".
const MEDIUM_REL_THRESHOLD_MS = 6 * 24 * 60 * 60 * 1000;

const formatRelativeShort = (isoString) => {
  if (!isoString) return "";
  const ts = Date.parse(isoString);
  if (Number.isNaN(ts)) return "";
  const delta = Date.now() - ts;
  if (delta < 0) return "now";
  if (delta < 60 * 1000) return "now";
  if (delta < SHORT_REL_THRESHOLD_MS) {
    const minutes = Math.floor(delta / 60000);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h`;
  }
  if (delta < MEDIUM_REL_THRESHOLD_MS) {
    const days = Math.floor(delta / SHORT_REL_THRESHOLD_MS);
    return `${days}d`;
  }
  const date = new Date(ts);
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

/**
 * Compact tooltip-friendly absolute timestamp for the row's `title`.
 * Surfaces the full date/time on hover so the relative chip stays
 * tight while still being unambiguous when an attorney is triaging
 * dozens of items.
 */
const formatAbsolute = (isoString) => {
  if (!isoString) return "";
  const ts = Date.parse(isoString);
  if (Number.isNaN(ts)) return "";
  const date = new Date(ts);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const isSnoozeActive = (isoString) => {
  if (!isoString) return false;
  const ts = Date.parse(isoString);
  if (Number.isNaN(ts)) return false;
  return ts > Date.now();
};

const InboxListItem = ({ item, selected = false, checked = false, onSelect }) => {
  const handleClick = (event) => {
    if (!item) return;
    onSelect(item.id, event);
  };

  const matter = item?.matter || null;
  const previewText = useMemo(() => {
    const preview = item?.briefing_preview;
    if (preview && preview.trim()) return preview;
    if (matter?.name) return `Mailbox · ${matter.name}`;
    return "Briefing pending…";
  }, [item?.briefing_preview, matter]);

  const isUnread = !item?.is_read;
  const snoozeActive = isSnoozeActive(item?.snoozed_until);
  const contextStatus = item?.context_analysis_status;
  const openTaskCount = Number(item?.open_task_count) || 0;
  const unreadMessageCount = Number(item?.unread_message_count) || 0;
  const receivedAt = item?.received_at;
  // Only the recently-reviewed list carries a review time, and there the
  // list is sorted by it — showing arrival ages instead would read as a
  // scrambled order.
  const reviewedAt = item?.reviewed_at || null;
  const stampAt = reviewedAt || receivedAt;

  // The row only carries the headline. The reason, the earlier
  // document's name and the dismiss/undo controls live in the right
  // rail, because a triage list is the wrong place to argue a case.
  const duplicateFlag = item?.duplicate_flag || null;
  const duplicateIsStrong = duplicateFlag?.strength === "strong";

  // Inbox delegation: this letter is in my inbox because I cover the
  // named person's mail. Names only — "For Chris Stratton" — so the row
  // reads as whose desk it is for, not as another action to take.
  const coveringFor = useMemo(() => {
    const people = Array.isArray(item?.covering_for) ? item.covering_for : [];
    return people
      .map((person) => (person?.name || person?.username || "").trim())
      .filter(Boolean);
  }, [item?.covering_for]);
  const coveringLabel = coveringFor.length ? `For ${coveringFor.join(", ")}` : "";

  return (
    <InboxRow
      type="button"
      $selected={selected}
      $checked={checked}
      onClick={handleClick}
      aria-current={selected ? "true" : undefined}
      aria-pressed={checked || selected ? "true" : undefined}
      aria-label={`${isUnread ? "Unread: " : ""}${item?.name || "Document"}${
        matter?.name ? ` — ${matter.name}` : ""
      }${coveringLabel ? ` — ${coveringLabel}` : ""}${
        duplicateFlag
          ? duplicateIsStrong
            ? " — already reviewed"
            : " — possibly already reviewed"
          : ""
      }`}
    >
      <InboxRowTopLine>
        <InboxRowUnreadDot $visible={isUnread} aria-hidden="true" />
        <InboxRowTitle $unread={isUnread}>
          {item?.name || "Untitled document"}
        </InboxRowTitle>
        <InboxRowTimestamp
          title={
            reviewedAt
              ? `Reviewed ${formatAbsolute(reviewedAt)} · arrived ${formatAbsolute(
                  receivedAt
                )}`
              : formatAbsolute(receivedAt)
          }
        >
          {formatRelativeShort(stampAt)}
        </InboxRowTimestamp>
      </InboxRowTopLine>

      <InboxRowPreview>{previewText}</InboxRowPreview>

      {(snoozeActive ||
        duplicateFlag ||
        coveringLabel ||
        contextStatus === "running" ||
        contextStatus === "pending" ||
        contextStatus === "failed" ||
        openTaskCount > 0 ||
        unreadMessageCount > 0) && (
        <InboxRowMetaRow>
          {coveringLabel && (
            <InboxRowChip
              $tone="coveringFor"
              title={`In your inbox because you cover ${coveringFor.join(", ")}'s mail`}
            >
              <UserSwitch size={11} weight="fill" />
              {coveringLabel}
            </InboxRowChip>
          )}
          {duplicateFlag && (
            <InboxRowChip
              $tone={duplicateIsStrong ? "duplicate" : "duplicateWeak"}
              title={duplicateFlag.reason || undefined}
            >
              <Copy size={11} weight="fill" />
              {duplicateIsStrong ? "Already reviewed" : "Possibly already reviewed"}
            </InboxRowChip>
          )}
          {snoozeActive && (
            <InboxRowChip $tone="snooze" title="Snoozed">
              <MoonStars size={11} weight="fill" />
              Snoozed
            </InboxRowChip>
          )}
          {contextStatus === "running" || contextStatus === "pending" ? (
            <InboxRowChip $tone="context" title="Context analysis running">
              <Sparkle size={11} weight="fill" />
              Analyzing
            </InboxRowChip>
          ) : null}
          {contextStatus === "failed" && (
            <InboxRowChip $tone="context" title="Context analysis failed">
              <Clock size={11} weight="fill" />
              Analysis failed
            </InboxRowChip>
          )}
          {openTaskCount > 0 && (
            <InboxRowChip
              $tone="task"
              title={`${openTaskCount} open task${openTaskCount === 1 ? "" : "s"}`}
            >
              <Note size={11} weight="fill" />
              {openTaskCount} task{openTaskCount === 1 ? "" : "s"}
            </InboxRowChip>
          )}
          {unreadMessageCount > 0 && (
            <InboxRowChip
              $tone="message"
              title={`${unreadMessageCount} unread message${unreadMessageCount === 1 ? "" : "s"}`}
            >
              <ChatCircleText size={11} weight="fill" />
              {unreadMessageCount} message
              {unreadMessageCount === 1 ? "" : "s"}
            </InboxRowChip>
          )}
        </InboxRowMetaRow>
      )}
    </InboxRow>
  );
};

InboxListItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    name: PropTypes.string,
    matter: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      matter_number: PropTypes.string,
    }),
    received_at: PropTypes.string,
    reviewed_at: PropTypes.string,
    is_read: PropTypes.bool,
    snoozed_until: PropTypes.string,
    briefing_preview: PropTypes.string,
    context_analysis_status: PropTypes.string,
    open_task_count: PropTypes.number,
    unread_message_count: PropTypes.number,
    duplicate_flag: PropTypes.shape({
      match_id: PropTypes.number,
      kind: PropTypes.string,
      strength: PropTypes.oneOf(["strong", "weak"]),
      reason: PropTypes.string,
    }),
    covering_for: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        name: PropTypes.string,
        username: PropTypes.string,
      })
    ),
  }).isRequired,
  selected: PropTypes.bool,
  checked: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

export default memo(InboxListItem);
